import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadType, ObjectType, TaskCardType } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';
import { LeadSelected } from 'modules/lead';
import { ObjectSelected } from 'modules/objects';
import TaskBindingEditForm from '../../../shared/TaskBindingEditForm';

interface TaskCardBindingSectionProps {
    task: TaskCardType;
    editing: boolean;
    objectValue: ObjectType | null;
    leadValue: LeadType | null;
    canEdit: boolean;
    onStartEdit: () => void;
    onCancel: () => void;
    onSaved: (task: TaskCardType) => void;
}

const ChangeButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
    <button type="button" className="btn btn-link p-0 align-baseline fs-12" onClick={onClick}>
        <SolarIcon name="pencil" className="me-1 fs-14" />
        {label}
    </button>
);

const TaskCardBindingSection = ({
    task,
    editing,
    objectValue,
    leadValue,
    canEdit,
    onStartEdit,
    onCancel,
    onSaved,
}: TaskCardBindingSectionProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const changeLabel = t('common:buttons.change');
    const changeButton: ReactNode = canEdit ? <ChangeButton onClick={onStartEdit} label={changeLabel} /> : null;

    return (
        <section className="task-card-section mb-3">
            <div className="task-card-section__label">{t('fields.taskFor')}</div>
            {editing ? (
                <div className="task-card-panel">
                    <TaskBindingEditForm task={task} onCancel={onCancel} onSaved={onSaved} />
                </div>
            ) : objectValue ? (
                <ObjectSelected
                    viewOnly
                    value={objectValue}
                    title={
                        Number(objectValue.onlyService)
                            ? t('fields.service')
                            : Number(objectValue.onlySale)
                              ? t('fields.sale')
                              : t('fields.object')
                    }
                    className="mb-0"
                    rightElement={changeButton}
                />
            ) : leadValue ? (
                <LeadSelected
                    viewOnly
                    value={leadValue}
                    title={t('fields.lead')}
                    className="mb-0"
                    rightElement={changeButton}
                />
            ) : (
                <div className="task-card-panel">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-2 text-muted">
                            <SolarIcon name="person" />
                            <span>{t('fields.personal')}</span>
                        </div>
                        {changeButton}
                    </div>
                </div>
            )}
        </section>
    );
};

export default TaskCardBindingSection;
